import { useCallback, useEffect, useRef, useState, useMemo } from 'react';

import "./jobseeker.css"
import Grid from '@mui/material/Grid2';
import * as echarts from 'echarts';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { apiService } from '../../../services/apiService';
import { useJobseekerReport } from './hooks/useJobseekerReport';
import { fetchJobseekerLanes, fetchJobseekerTableRows } from './services/jobseekerListService';
import { OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap';
import { faCircleCheck, faExclamationCircle, faLock, faPhone, faChevronDown, faAngleRight, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import { faCopy, faEnvelope, faUser } from '@fortawesome/free-regular-svg-icons';
import { toast } from 'react-toastify';

const firebaseConfig = {
  apiKey: "AIzaSyD02rMLZSXId5gXEgowqt-N8f_gPyCYuxU",
  authDomain: "bench-track.firebaseapp.com",
  projectId: "bench-track",
  storageBucket: "bench-track.appspot.com",
  messagingSenderId: "18153078782",
  appId: "1:18153078782:web:1c312dbf6947ccae200aaf",
  measurementId: "G-VG4G8QERNK"
};

const THEME_BLUE = '#1F5AA6';
const THEME_PINK = '#C72B7A';

function hexToRgbTuple(hex) {
    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
}
function blendThemeHex(t) {
    const a = hexToRgbTuple(THEME_BLUE);
    const b = hexToRgbTuple(THEME_PINK);
    const r = Math.round(a.r + (b.r - a.r) * t);
    const g = Math.round(a.g + (b.g - a.g) * t);
    const bl = Math.round(a.b + (b.b - a.b) * t);
    return `#${[r, g, bl].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function buildThemePalette(n) {
    if (n <= 1) return [THEME_BLUE];
    const arr = [];
    for (let i = 0; i < n; i++) arr.push(blendThemeHex(i / (n - 1)));
    return arr;
}

function hexToHsl(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
        s = d / (1 - Math.abs(2 * l - 1));
        switch (max) {
            case r: h = ((g - b) / d) % 6; break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4;
        }
        h *= 60;
        if (h < 0) h += 360;
    }
    return [h, s, l];
}

function hslToHex(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function shadeRamp(baseHex, steps) {
    const [h, s] = hexToHsl(baseHex);
    if (steps <= 1) return [baseHex];
    const lightStart = 0.8;
    const lightEnd = 0.34;
    const colors = [];
    for (let i = 0; i < steps; i++) {
        const l = lightStart - ((lightStart - lightEnd) * i) / (steps - 1);
        colors.push(hslToHex(h, Math.max(s, 0.35), l));
    }
    return colors;
}

const EMPTY_BREAKDOWN = [];
const DEFAULT_PROFILE_IMAGE = require('../../../assets/defaultprofile.svg').default;

// Training Status and Onboarding Hold are NOT grouping dimensions — same as
// the Jobseekers Lane view, they're cross-cutting filters (Include/Exclude/
// Only) applied on top of whichever tab below is showing, not tabs of their
// own. Keeping them out of REPORT_TABS (and instead rendering them via
// QUICK_FILTERS in the header row) is what keeps this report's numbers
// identical to the lane view's for the same filter combination.
const REPORT_TABS = [
    { key: 'stage', label: 'Stage & Sub-Stage' },
    { key: 'visa', label: 'Visa Type' },
    { key: 'manager', label: 'Placement Manager' },
];

// Onboarding Hold / Training Status share this same Include/Exclude/Only
// set, rendered as a 3-way toggle rather than a <select>.
const EASY_FILTER_MODES = [
    { key: 'include', label: 'Include' },
    { key: 'exclude', label: 'Exclude' },
    { key: 'only', label: 'Only' },
];

// DROPDOWN FILTERS - For the right side dropdown
const INTAKE_DROPDOWN_FILTERS = [
    { key: 'this_week', label: 'This Week' },
    { key: 'last_week', label: 'Last Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'this_year', label: 'This Year' },
    { key: 'last_year', label: 'Last Year' },
    { key: 'custom', label: 'Custom Range' },
];

function Jobseeker() {
    const navigate = useNavigate();
    const [onboardStatus, setOnboardStatus] = useState([]);
    const [progressBar, setProgressbar] = useState({});
    const Role_user = localStorage.getItem('role');
    const allUserData = JSON.parse(localStorage.getItem('user_alldata'));
    const permissionData = JSON.parse(localStorage.getItem('permissions'));
    const pipelineChartRef = useRef(null);
    const pipelineChartInstance = useRef(null);
    const tabsScrollRef = useRef(null);
    const dropdownRef = useRef(null);

    const [intakeFilter, setIntakeFilter] = useState('this_week');
    const [intakeCustomStart, setIntakeCustomStart] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [intakeCustomEnd, setIntakeCustomEnd] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [reportTab, setReportTab] = useState('stage');
    const [holdMode, setHoldMode] = useState('include');
    const [trainingMode, setTrainingMode] = useState('include');
    const [showFilters, setShowFilters] = useState(false);
    const [activeSegment, setActiveSegment] = useState(null);
    const [expandedStage, setExpandedStage] = useState(null);
    const [showAllSubStages, setShowAllSubStages] = useState(false);
    const [pipelineAsOf] = useState(() => new Date());
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const visaChartRef = useRef(null);
    const visaChartInstance = useRef(null);

    const isIntakeCustom = intakeFilter === 'custom';
    const isReportEnabled = Role_user !== 'job_seeker';
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const reportParams = useMemo(() => {
        const params = {
            granularity: intakeFilter,
            dimensions: ['stage', 'sub_stage', 'visa', 'manager', 'training_status', 'onboarding_hold'],
        };
        if (isIntakeCustom) {
            params.start_date = intakeCustomStart;
            params.end_date = intakeCustomEnd;
        }
        if (holdMode !== 'include') params.onboarding_hold = holdMode;
        if (trainingMode !== 'include') params.training_status = trainingMode;
        return params;
    }, [intakeFilter, isIntakeCustom, intakeCustomStart, intakeCustomEnd, holdMode, trainingMode]);

    const {
        data: reportData,
        loading: reportLoading,
        error: reportError,
        refetch: refetchReport,
    } = useJobseekerReport(reportParams, { enabled: isReportEnabled });

    const intakeStats = useMemo(() => {
        const trend = reportData?.intake_trend;
        const categories = trend?.categories || [];
        const values = trend?.values || [];
        const total = values.reduce((sum, v) => sum + v, 0);
        const average = values.length ? Math.round(total / values.length) : 0;

        let peakIndex = 0;
        values.forEach((v, i) => { if (v > values[peakIndex]) peakIndex = i; });

        return {
            categories,
            values,
            total,
            average,
            peakLabel: categories[peakIndex] || '—',
            peakValue: values[peakIndex] || 0,
            rangeLabel: trend?.range_label || '',
            granularity: trend?.granularity === 'year' ? 'Yearly'
                : trend?.granularity === 'half_year' ? 'Half-Yearly'
                : trend?.granularity === 'month' ? 'Monthly'
                : trend?.granularity === 'week' ? 'Weekly'
                : 'Daily',
        };
    }, [reportData]);

    const currentFilterLabel = useMemo(() => {
        const found = INTAKE_DROPDOWN_FILTERS.find(f => f.key === intakeFilter);
        return found ? found.label : 'Select Period';
    }, [intakeFilter]);

    const hasReportData = Boolean(reportData);

    useEffect(() => {
        if (!isReportEnabled) return;
        if (!pipelineChartRef.current) return;

        const chart = echarts.init(pipelineChartRef.current);
        pipelineChartInstance.current = chart;

        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => chart.resize());
            ro.observe(pipelineChartRef.current);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (ro) ro.disconnect();
            chart.dispose();
            pipelineChartInstance.current = null;
        };
    }, [isReportEnabled, hasReportData]);

    useEffect(() => {
        const chart = pipelineChartInstance.current;
        if (!chart) return;
        if (!intakeStats.categories.length) { chart.clear(); return; }

        const { categories, values, granularity } = intakeStats;
        const seriesLabel = `${granularity} actual`;
        const maxVal = Math.max(...values, 0);
        const niceStep = maxVal <= 4 ? 2 : Math.ceil(Math.ceil(maxVal * 1.2) / 4 / 2) * 2 || 2;
        let yMax = niceStep * Math.max(4, Math.ceil((maxVal * 1.2) / niceStep));
        while (yMax < maxVal) yMax += niceStep;
        pipelineChartRef.current.style.marginLeft = '';
        pipelineChartRef.current.style.width = '';
        chart.resize();
        const chartWidth = chart.getWidth() || pipelineChartRef.current.clientWidth || 300;
        const maxLabelLen = categories.reduce((m, c) => Math.max(m, String(c).length), 0);
        const approxLabelPx = maxLabelLen * 5.6 + 6;
        const needsRotate = categories.length > 8 || maxLabelLen > 10 ||
            (approxLabelPx * categories.length) > chartWidth * 1.3;
        const maxLabelsThatFit = Math.max(1, Math.floor(chartWidth / (approxLabelPx * (needsRotate ? 0.65 : 1))));
        const labelStep = categories.length > maxLabelsThatFit
            ? Math.ceil(categories.length / maxLabelsThatFit)
            : 1;
        // A rotated label is centered on its own category band, so category
        // 0 sits close enough to the plot's left edge that a wide rotated
        // label (a custom-range date span) can swing past the canvas
        // boundary and get clipped there. This only actually happens for
        // Custom Range (the only granularity with labels this wide) — This
        // Month/Last Month etc. keep labeling every data point as before.
        // Skipping category 0 shifts the alternating pattern below to start
        // counting from category 1, landing on 1, 3, 5... instead of 0, 2,
        // 4 — the last category is always shown regardless of the pattern
        // so the range doesn't visually stop short of its own end.
        const skipEdgeLabels = isIntakeCustom && needsRotate && categories.length > 2;

        chart.setOption({
            grid: {
                left: 8,
                right: 10,
                top: 32,
                bottom: needsRotate ? 34 : 14,
                containLabel: true
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                    shadowStyle: { color: 'rgba(199, 43, 122, 0.06)' },
                },
                backgroundColor: '#FFFFFF',
                borderColor: '#F0E3EC',
                borderWidth: 1,
                padding: 0,
                extraCssText: 'box-shadow: 0 8px 20px -6px rgba(16,24,40,0.12); border-radius: 8px; overflow: hidden;',
                formatter: function (params) {
                    const barPt = params.find((p) => p.seriesName === seriesLabel);
                    const curVal = barPt ? barPt.value : 0;
                    return `
                        <div style="min-width:140px;font-family:inherit;">
                            <div style="padding:8px 12px;background:#FBF5F9;border-bottom:1px solid #F0E3EC;">
                                <span style="font-weight:700;font-size:12px;color:#101828;">${params[0].axisValue}</span>
                            </div>
                            <div style="padding:8px 12px 9px;display:flex;flex-direction:column;gap:6px;">
                                <div style="display:flex;align-items:center;justify-content:space-between;gap:18px;">
                                    <span style="display:flex;align-items:center;gap:6px;font-size:11px;color:#6B7280;font-weight:500;">
                                        <i style="width:7px;height:7px;border-radius:2px;background:rgba(199,43,122,0.4);display:inline-block;"></i>
                                        ${seriesLabel}
                                    </span>
                                    <span style="font-weight:700;font-size:12px;color:#101828;font-variant-numeric:tabular-nums;">${curVal}</span>
                                </div>

                            </div>
                        </div>`;
                }
            },
            legend: { show: false },
            xAxis: {
                type: 'category',
                boundaryGap: true,
                data: categories,
                axisLine: { lineStyle: { color: '#EDEDEC', width: 1 } },
                axisTick: { show: false },
                axisLabel: {
                    color: '#8993A6',
                    fontSize: 10.5,
                    fontWeight: 500,
                    interval: skipEdgeLabels
                        ? (idx) => idx === categories.length - 1 || (idx !== 0 && (idx - 1) % labelStep === 0)
                        : labelStep - 1,
                    rotate: needsRotate ? 15 : 0,
                    margin: 12
                },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yMax,
                interval: niceStep,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    lineStyle: { color: '#EDEDEC', type: 'solid', width: 1 }
                },
                axisLabel: {
                    color: '#ACB3C0',
                    fontSize: 10,
                    fontWeight: 500,
                    padding: [0, 8, 0, 0]
                }
            },
            series: [
                {
                    name: seriesLabel,
                    type: 'bar',
                    barWidth: '34%',
                    barMaxWidth: 13,
                    itemStyle: {
                        borderRadius: [4, 4, 0, 0],
                        color: 'rgba(199, 43, 122, 0.32)',
                    },
                    emphasis: {
                        itemStyle: {
                            color: 'rgba(199, 43, 122, 0.48)',
                        }
                    },
                    z: 2,
                    data: values,
                    animationDelay: function (idx) { return idx * 40; },
                    animationEasing: 'cubicOut',
                },
                {
                    name: 'trend',
                    type: 'line',
                    smooth: 0.8,
                    smoothMonotone: 'x',
                    symbol: 'circle',
                    symbolSize: 6,
                    showSymbol: true,
                    z: 3,
                    lineStyle: {
                        width: 2,
                        color: THEME_PINK,
                    },
                    areaStyle: {
                        color: {
                            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(199, 43, 122, 0.20)' },
                                { offset: 0.55, color: 'rgba(199, 43, 122, 0.06)' },
                                { offset: 1, color: 'rgba(199, 43, 122, 0)' }
                            ]
                        }
                    },
                    itemStyle: {
                        color: '#FFFFFF',
                        borderColor: THEME_PINK,
                        borderWidth: 2,
                    },
                    emphasis: {
                        focus: 'series',
                        scale: 1.3,
                        itemStyle: { borderWidth: 2.5 },
                    },
                    data: values,
                    animationDelay: function (idx) { return idx * 40 + 180; },
                    animationEasing: 'cubicOut',
                }
            ],
            animationDuration: 700,
            animationEasing: 'cubicOut',
        });
    }, [intakeStats, isIntakeCustom]);
    const stageBreakdown = reportData?.breakdowns?.stage || EMPTY_BREAKDOWN;
    const subStageBreakdown = reportData?.breakdowns?.sub_stage || EMPTY_BREAKDOWN;
    const visaBreakdown = reportData?.breakdowns?.visa || EMPTY_BREAKDOWN;
    const managerBreakdown = reportData?.breakdowns?.manager || EMPTY_BREAKDOWN;
    const trainingBreakdown = reportData?.breakdowns?.training_status || EMPTY_BREAKDOWN;
    const holdBreakdown = reportData?.breakdowns?.onboarding_hold || EMPTY_BREAKDOWN;
    const reportTotal = reportData?.total || 0;

    const standardCount = holdBreakdown.find((r) => r.value === 'Not On Hold')?.count || 0;
    const onHoldCount = holdBreakdown.find((r) => r.value === 'On Hold')?.count || 0;
    const stageRows = useMemo(() => {
        const palette = buildThemePalette(Math.max(stageBreakdown.length, 1));
        return stageBreakdown.map((row, idx) => {
            const segments = subStageBreakdown
                .filter((s) => s.stage === row.value)
                .map((s) => ({ label: s.value, count: s.count, pct: s.pct }));
            const ramp = shadeRamp(palette[idx] || THEME_BLUE, Math.max(segments.length, 1));
            return {
                stage: row.value,
                count: row.count,
                pct: row.pct,
                segments: segments.map((seg, i) => ({ ...seg, color: ramp[i] })),
            };
        });
    }, [stageBreakdown, subStageBreakdown]);

    const currentStageRow = useMemo(
        () => stageRows.find((r) => r.stage === expandedStage) || null,
        [stageRows, expandedStage]
    );

    // Every sub-stage across every stage, flattened into one list — kept in
    // the same stage order as the "Volume by Stage" list above (not
    // re-sorted by count), so a sub-stage's position here still reflects
    // where its parent stage sits in the pipeline. Each row keeps the color
    // it already has as part of its own stage's segment ramp, so the color
    // still hints at which stage it belongs to.
    const allSubStagesFlat = useMemo(
        () => stageRows.flatMap((row) => row.segments.map((seg) => ({ ...seg, stage: row.stage }))),
        [stageRows]
    );

    const [segmentPanel, setSegmentPanel] = useState(null);
    const segmentRequestIdRef = useRef(0);

    useEffect(() => {
        setActiveSegment(null);
        setExpandedStage(null);
        setShowAllSubStages(false);
        setSegmentPanel(null);
    }, [reportTab, holdMode, trainingMode]);

    const easyFilterParams = useMemo(() => {
        const params = {};
        if (holdMode !== 'include') params.onboarding_hold = holdMode;
        if (trainingMode !== 'include') params.training_status = trainingMode;
        return params;
    }, [holdMode, trainingMode]);

    // Backend formats an unset placement manager as the literal string '-'
    // rather than null — treat that the same as no value here.
    const cleanValue = (v) => (v && v !== '-' ? v : null);

    const normalizeLaneRow = (raw) => ({
        id: raw.id,
        formatId: raw.format_id,
        name: raw.name,
        profile: raw.profile,
        visa: raw.visa,
        manager: cleanValue(raw.placement_manager),
        onHold: raw.onboarding_case_status === 'hold',
    });

    const normalizeTableRow = (raw) => ({
        id: raw.id,
        formatId: raw.format_id,
        name: raw.name,
        profile: raw.profile,
        visa: raw.citizenship_status,
        manager: cleanValue(raw.user_details?.placement_manager),
        onHold: raw.current_onboarding_status === 'hold',
    });

    const buildDrillFilter = (kind, value) => {
        if (kind === 'visa') {
            return value === 'Unknown'
                ? [{ column: 'citizenship_status', operator: 'is_empty' }]
                : [{ column: 'citizenship_status', operator: 'is', value }];
        }
        if (kind === 'manager') {
            return value === 'Unassigned'
                ? [{ column: 'placement_manager', operator: 'is_empty' }]
                : [{ column: 'placement_manager', operator: 'is', value }];
        }
        return [];
    };

    const openSegment = async (kind, label, groupBy, fetchFn, extract) => {
        const requestId = ++segmentRequestIdRef.current;
        setSegmentPanel({ kind, label, groupBy, rows: [], total: 0, page: 1, hasMore: false, loading: true, error: null });
        try {
            const response = await fetchFn();
            if (requestId !== segmentRequestIdRef.current) return;
            if (response?.success) {
                setSegmentPanel({ kind, label, groupBy, loading: false, error: null, ...extract(response) });
            } else if (response?.authenticate === false) {
                await apiService.postRequest('/auth/logout');
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
            } else {
                setSegmentPanel((prev) => (prev && prev.kind === kind && prev.label === label ? { ...prev, loading: false, error: 'Unable to load jobseekers' } : prev));
            }
        } catch (e) {
            if (requestId === segmentRequestIdRef.current) {
                setSegmentPanel((prev) => (prev && prev.kind === kind && prev.label === label ? { ...prev, loading: false, error: 'Unable to load jobseekers' } : prev));
            }
        }
    };    
    const findLane = (response, label) => {
        const matches = (response.columns || []).filter((c) => c.title === label);
        if (matches.length === 0) return null;
        if (matches.length === 1) return matches[0];
        return {
            group_id: matches[0].group_id,
            title: label,
            total: matches.reduce((sum, m) => sum + (m.total || 0), 0),
            page: matches[0].page,
            has_more: matches.some((m) => m.has_more),
            data: matches.flatMap((m) => m.data || []),
        };
    };

    const openStageSegment = (stageName) => openSegment('stage', stageName, 'stage',
        () => fetchJobseekerLanes('stage', easyFilterParams),
        (response) => {
            const lane = findLane(response, stageName);
            return { rows: (lane?.data || []).map(normalizeLaneRow), total: lane?.total || 0, page: lane?.page || 1, hasMore: !!lane?.has_more, laneGroupId: lane?.group_id || stageName };
        }
    );

    const openSubStageSegment = (subStageName) => openSegment('sub_stage', subStageName, 'sub_stage',
        () => fetchJobseekerLanes('sub_stage', easyFilterParams),
        (response) => {
            const lane = findLane(response, subStageName);
            return { rows: (lane?.data || []).map(normalizeLaneRow), total: lane?.total || 0, page: lane?.page || 1, hasMore: !!lane?.has_more, laneGroupId: lane?.group_id || subStageName };
        }
    );

    const openTableSegment = (kind, value) => openSegment(kind, value, null,
        () => fetchJobseekerTableRows(buildDrillFilter(kind, value), easyFilterParams),
        (response) => ({
            rows: (response.data || []).map(normalizeTableRow),
            total: response.meta?.total ?? (response.data || []).length,
            page: response.meta?.current_page || 1,
            hasMore: (response.meta?.current_page || 1) < (response.meta?.last_page || 1),
        })
    );

    const loadMoreSegment = async () => {
        if (!segmentPanel || segmentPanel.loading || !segmentPanel.hasMore) return;
        const { kind, label, groupBy, laneGroupId, page, rows } = segmentPanel;
        const requestId = ++segmentRequestIdRef.current;
        setSegmentPanel((prev) => ({ ...prev, loading: true }));
        try {
            let appended, total, nextPage, hasMore;
            if (groupBy) {
                const response = await fetchJobseekerLanes(groupBy, easyFilterParams, { [laneGroupId]: page + 1 });
                if (requestId !== segmentRequestIdRef.current) return;
                const lane = findLane(response, label);
                appended = rows.concat((lane?.data || []).map(normalizeLaneRow));
                total = lane?.total ?? segmentPanel.total;
                nextPage = lane?.page ?? page + 1;
                hasMore = !!lane?.has_more;
            } else {
                const response = await fetchJobseekerTableRows(buildDrillFilter(kind, label), easyFilterParams, 8, page + 1);
                if (requestId !== segmentRequestIdRef.current) return;
                appended = rows.concat((response?.data || []).map(normalizeTableRow));
                total = response?.meta?.total ?? segmentPanel.total;
                nextPage = response?.meta?.current_page ?? page + 1;
                hasMore = (response?.meta?.current_page || 1) < (response?.meta?.last_page || 1);
            }
            setSegmentPanel((prev) => (prev && prev.kind === kind && prev.label === label
                ? { ...prev, rows: appended, total, page: nextPage, hasMore, loading: false }
                : prev));
        } catch (e) {
            setSegmentPanel((prev) => (prev ? { ...prev, loading: false } : prev));
        }
    };
    const loadMoreSegmentRef = useRef(loadMoreSegment);
    useEffect(() => { loadMoreSegmentRef.current = loadMoreSegment; });

    const segmentSentinelRef = useRef(null);
    const activeSegmentKind = segmentPanel?.kind;
    const activeSegmentLabel = segmentPanel?.label;
    const activeSegmentHasMore = segmentPanel?.hasMore;
    useEffect(() => {
        if (!activeSegmentHasMore) return undefined;
        const el = segmentSentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                loadMoreSegmentRef.current();
            }
        }, { threshold: 0.1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [activeSegmentKind, activeSegmentLabel, activeSegmentHasMore]);

    const scrollTabs = (dir) => {
        if (!tabsScrollRef.current) return;
        tabsScrollRef.current.scrollBy({ left: dir * 120, behavior: 'smooth' });
    };

    // Scroll arrows only make sense once the tab strip is actually wider than
    // its container — showing them unconditionally (as with 5 tabs before)
    // reserved dead space on both sides of the strip even when nothing
    // needed scrolling, throwing off the tab row's alignment against the
    // panel content beneath it.
    const [showTabArrows, setShowTabArrows] = useState(false);
    useEffect(() => {
        const el = tabsScrollRef.current;
        if (!el) return undefined;
        // Toggling the arrows changes this same element's width, which would
        // otherwise re-trigger the observer within the same delivery cycle
        // (the browser's "ResizeObserver loop completed with undelivered
        // notifications" warning). Deferring the state update to the next
        // frame lets that cycle finish first.
        let rafId = null;
        const checkOverflow = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => setShowTabArrows(el.scrollWidth > el.clientWidth + 1));
        };
        checkOverflow();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkOverflow) : null;
        ro?.observe(el);
        window.addEventListener('resize', checkOverflow);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            ro?.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [isReportEnabled, hasReportData]);

    const handleBreakdownRowClick = (kind, value) => {
        const isSame = activeSegment && activeSegment.type === kind && activeSegment.value === value;
        if (isSame) {
            setActiveSegment(null);
            setSegmentPanel(null);
            return;
        }
        setActiveSegment({ type: kind, value });
        openTableSegment(kind, value);
    };
    const handleStageRowClick = (stageName) => {
        const collapsing = expandedStage === stageName;
        setExpandedStage(collapsing ? null : stageName);
        setShowAllSubStages(false);
        setActiveSegment(null);
        if (collapsing) {
            setSegmentPanel(null);
        } else {
            openStageSegment(stageName);
        }
    };
    const handleSegmentClick = (stageName, subLabel) => {
        setExpandedStage(stageName);
        setShowAllSubStages(false);
        setActiveSegment({ type: 'subStage', stage: stageName, value: subLabel });
        openSubStageSegment(subLabel);
    };

    const handleCollapseStage = () => {
        setExpandedStage(null);
        setActiveSegment(null);
        setSegmentPanel(null);
    };

    const handleViewAllSubStages = () => {
        setExpandedStage(null);
        setShowAllSubStages(true);
        setActiveSegment(null);
        setSegmentPanel(null);
    };

    const handleExitAllSubStages = () => {
        setShowAllSubStages(false);
        setActiveSegment(null);
        setSegmentPanel(null);
    };

    const toggleSubStage = (stageName, value) => {
        const isSame = activeSegment && activeSegment.type === 'subStage' && activeSegment.stage === stageName && activeSegment.value === value;
        if (isSame) {
            setActiveSegment(null);
            openStageSegment(stageName); // fall back to the parent stage's list
            return;
        }
        setActiveSegment({ type: 'subStage', stage: stageName, value });
        openSubStageSegment(value);
    };

    const handleFilterSelect = (key) => {
        setIntakeFilter(key);
        setIsDropdownOpen(false);
    };
    const handleBreakdownRowClickRef = useRef(handleBreakdownRowClick);
    useEffect(() => { handleBreakdownRowClickRef.current = handleBreakdownRowClick; });

    useEffect(() => {
        if (reportTab !== 'visa' || !visaChartRef.current) return;
        const chart = echarts.init(visaChartRef.current);
        visaChartInstance.current = chart;
        const handleClick = (params) => { if (params.name) handleBreakdownRowClickRef.current('visa', params.name); };
        chart.on('click', handleClick);
        const handleResize = () => chart.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            chart.off('click', handleClick);
            window.removeEventListener('resize', handleResize);
            chart.dispose();
            visaChartInstance.current = null;
        };
    }, [reportTab]);

    useEffect(() => {
        const chart = visaChartInstance.current;
        if (!chart || reportTab !== 'visa') return;
        const palette = buildThemePalette(visaBreakdown.length);
        chart.setOption({
            tooltip: {
                trigger: 'item',
                backgroundColor: '#FFFFFF',
                borderColor: '#E7EAF2',
                borderWidth: 1,
                textStyle: { color: '#101828', fontSize: 11 },
                formatter: (p) => `<b>${p.name}</b><br/>${p.value} · ${p.percent}%`,
                extraCssText: 'box-shadow: 0 2px 12px rgba(0,0,0,0.06); border-radius: 6px; padding: 6px 10px;'
            },
            series: [{
                type: 'pie',
                radius: ['62%', '92%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: false,
                label: { show: false },
                labelLine: { show: false },
                itemStyle: { borderColor: '#FFFFFF', borderWidth: 2 },
                emphasis: { scaleSize: 6 },
                data: visaBreakdown.map((row, idx) => ({
                    name: row.value,
                    value: row.count,
                    itemStyle: {
                        color: palette[idx],
                        opacity: activeSegment && activeSegment.type === 'visa' && activeSegment.value !== row.value ? 0.32 : 1,
                    },
                })),
                animationDuration: 400,
            }],
        });
    }, [visaBreakdown, activeSegment, reportTab]);

    useEffect(() => {
        const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        const requestPermission = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
            console.log('Notification permission granted.');
            const currentToken = await getToken(messaging, { vapidKey: 'BJ4G6kSXuE2mnML3InkqKeCINeNXQyukVAptiyMLH3ZHvfe91ebLgbrX-9uVGJPyVqB1PUks9qxelSRA9PleUys' });
            if (currentToken) {
                sendTokenToServer(currentToken);
            } else {
                console.log('No registration token available.');
            }
            } else {
            console.log('Unable to get permission to notify.');
            }
        } catch (error) {
            console.error('Error getting permission or token', error);
        }
        };

        requestPermission();

        onMessage(messaging, (payload) => {
        const notificationTitle = payload.notification.title;
        const notificationOptions = {
            body: payload.notification.body,
            icon: payload.notification.icon,
        };
        new Notification(notificationTitle, notificationOptions);
        });
    }, []);

    const sendTokenToServer = async (token) => {
        try {
        const response = await apiService.postRequest('/save-device-token', { device_token:token });
        console.log('Token saved successfully:', response.data);
        } catch (error) {
        console.error('Error saving token:', error);
        }
    };

    const [showPaaI94, setShowPassI94] = useState('');
    useEffect(() => { 
        const fetchsetsData = async () => {
            const response = await apiService.postRequest('/jobseeker/onboarding/status');
            if (response && response.success) {
                setOnboardStatus(response.status);
                setProgressbar(response.stage_progress);
                setShowPassI94(response.jobseeker_validation_type);
            }else{
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        };
        fetchsetsData();
    }, []);

    const [loading, setLoading] = useState(true);
    const [jobslist, setJobs] = useState([]);
    const [articles, setArticles] = useState([]);
    const [caseData, setCaseData] = useState([]);
    const [taskData, setTaskData] = useState([]);

    const fetchJobs = useCallback(async () => {
        try {
            const response = await apiService.getRequest(`/recent-viewed-job-posts`);
            if (response?.success === true) {
                setJobs(response.data || []);
            } else if (response?.authenticate === false) {
                await apiService.postRequest('/auth/logout');
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchJobs(),
                    apiService.getRequest(`/recent-viewed-artcles`).then(res => {
                        if (res?.success === true) setArticles(res.data);
                        else if (res?.authenticate === false) handleLogout();
                    }),
                    apiService.getRequest(`/my-pending-cases`).then(res => {
                        if (res?.success === true) setCaseData(res.data);
                        else if (res?.authenticate === false) handleLogout();
                    }),
                    apiService.getRequest(`/my-pending-tasks`).then(res => {
                        if (res?.success === true) setTaskData(res.data);
                        else if (res?.authenticate === false) handleLogout();
                    }),
                ]);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [fetchJobs]);

    const handleLogout = async () => {
        await apiService.postRequest('/auth/logout');
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
    };

    const handleSave = async (saveid) => {
        try {
            setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: true } : job));
            const response = await apiService.postRequest('/jobseeker/save', { job_id: saveid });
            if (response?.success === true) {
                toast(response.message || 'Job saved successfully!');
                fetchJobs();
            } else {
                setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: false } : job));
                toast(response?.message || 'Failed, try again!');
                if (response?.authenticate === false) handleLogout();
            }
        } catch (err) {
            setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: false } : job));
            toast.error("Failed to save job");
        }
    };

    const handleRemoveSave = async (saveid) => {
        try {
            setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: false } : job));
            const response = await apiService.deleteRequest(`/jobseeker/remove-saved/${saveid}`);
            if (response?.success === true) {
                toast(response.message || 'Job Unsaved successfully!');
                fetchJobs();
            } else {
                setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: true } : job));
                toast(response?.message || 'Failed, try again!');
                if (response?.authenticate === false) handleLogout();
            }
        } catch (err) {
            setJobs(prev => prev.map(job => job.id === saveid ? { ...job, is_saved: true } : job));
            toast.error("Failed to Unsave job");
        }
    };

    const [showAll, setShowAll] = useState(false);
    const visibleJobs = showAll ? jobslist : jobslist?.slice(0, 3);
    const [showAllArticles, setShowAllArticles] = useState(false);
    const visibleArticles = showAllArticles ? articles : articles?.slice(0, 3);

  return (
    <div className='container-fluid px-0'>
        <div className='content_container'>
            <Grid size={{xs:12}} container className="main_jobseeker pb-4">
                {Role_user &&
                <>{Role_user === "job_seeker" ?
                    <Grid size={{xs:12}} container justifyContent="center">
                        <Grid size={{xs:12}} container justifyContent="center" className="py-4 jobseekdashback">
                            <h2 className='my-3'>Welcome, {allUserData?.first_name + " " + allUserData?.last_name}</h2>
                        </Grid>

                        <Grid size={{xs:12}} container justifyContent="space-between" className="mt-2">
                            <h4 className='my-3 w-100 fw-semibold'>Quick Links</h4>
                            <Grid size={{xs:2.8}} container justifyContent="center" alignItems="flex-end" className="dash_quicklinks dashlinkbg1">
                                {permissionData && permissionData?.manage_case_submitter === true ?
                                <Link to={"/catalogs/global-services"} className='link_absolute_dash'/>:
                                <div className='d-flex justify-content-center align-items-center lock_divpermission'>
                                    <FontAwesomeIcon icon={faLock} />
                                </div>}
                                <h4>Request Something</h4>
                            </Grid>
                            <Grid size={{xs:2.8}} container justifyContent="center" alignItems="flex-end" className="dash_quicklinks dashlinkbg2">
                                {permissionData && permissionData?.manage_knowledge_article === true ?
                                <Link to={"/knowledge"} className='link_absolute_dash'/>:
                                <div className='d-flex justify-content-center align-items-center lock_divpermission'>
                                    <FontAwesomeIcon icon={faLock} />
                                </div>}
                                <h4>Find Information</h4>
                            </Grid>
                            <Grid size={{xs:2.8}} container justifyContent="center" alignItems="flex-end" className="dash_quicklinks dashlinkbg3">
                                {permissionData && permissionData?.manage_job_posts === true && permissionData?.manage_job_post_viewer === true ?
                                <Link to={"/job-board/job-list"} className='link_absolute_dash'/>:
                                <div className='d-flex justify-content-center align-items-center lock_divpermission'>
                                    <FontAwesomeIcon icon={faLock} />
                                </div>}
                                <h4>ServiceNow Jobs</h4>
                            </Grid>
                            <Grid size={{xs:2.8}} container justifyContent="center" alignItems="flex-end" className="dash_quicklinks dashlinkbg4">
                                {permissionData && permissionData?.manage_employee_reviews === true ?
                                <Link to={"/employee-reviews"} className='link_absolute_dash'/>:
                                <div className='d-flex justify-content-center align-items-center lock_divpermission'>
                                    <FontAwesomeIcon icon={faLock} />
                                </div>}
                                <h4>Our Team</h4>
                            </Grid>
                        </Grid>
                        <Grid size={{xs:12}} container justifyContent="center" className="mt-4 pink_dashbg p-2 pb-3">
                            <h5 className='w-100 fw-semibold'>Onboarding Tracker</h5>
                            
                            <Grid size={{xs:12}} container justifyContent="center" className="white_progressbg mt-2">
                                <Grid size={{md:12,xs:12}} container className="pt-3">
                                    <Grid size={{md:2.4}} container flexDirection="column" alignItems="center" className="progress_dash">
                                        <ProgressBar now={progressBar.signup} className='progress_divdashses'/>
                                        <span className={`rownderspan ${progressBar.signup === 100 && 'completedashspan'}`}><b>1</b></span>
                                        <h6>Signup</h6>
                                        <div className='hoverdashshow'>
                                            <div className='d-flex flex-column dashshowersspan'>
                                                <div className='inner_contdiv_dash'>
                                                    <span>
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>
                                                        Terms & Conditions
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                Review and accept terms to proceed with  your onboarding.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["i94-validation"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["i94-validation"] === "not-started" && onboardStatus["i94-validation"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["i94-validation"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["i94-validation"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["i94-validation"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["i94-validation"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        {showPaaI94 && showPaaI94 === "i94_validation" ? "I94 Validation" : "Passport Validation"}
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is to verify your {showPaaI94 && showPaaI94 === "i94_validation" ? "I-94 travel" : "passport"} record and confirm your immigration details.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid size={{md:2.4}} container flexDirection="column" alignItems="center" className="progress_dash">
                                        <ProgressBar now={progressBar.referral_management} className='progress_divdashses'/>
                                        <span className={`rownderspan ${progressBar.referral_management === 100 && 'completedashspan'}`}><b>2</b></span>
                                        <h6>Referral Management</h6>
                                        <div className='hoverdashshow'>
                                            <div className='d-flex flex-column dashshowersspan'>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["verify-member-details"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["verify-member-details"] === "not-started" && onboardStatus["verify-member-details"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["verify-member-details"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["verify-member-details"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["verify-member-details"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["verify-member-details"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Verify Referral Details
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is to review the details of the person who referred you
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["member-confirmation"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["member-confirmation"] === "not-started" && onboardStatus["member-confirmation"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["member-confirmation"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["member-confirmation"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["member-confirmation"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["member-confirmation"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Referrer Confirmation
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This step is to confirm your referral and continue with the onboarding process.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["referral-investigation"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["referral-investigation"] === "not-started" && onboardStatus["referral-investigation"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["referral-investigation"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["referral-investigation"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["referral-investigation"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["referral-investigation"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Investigation
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is used to investigate whether the referral information provided was falsified by you or submitted mistakenly by the referring agent.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid size={{md:2.4}} container flexDirection="column" alignItems="center" className="progress_dash">
                                        <ProgressBar now={progressBar.service_selection} className='progress_divdashses'/>
                                        <span className={`rownderspan ${progressBar.service_selection === 100 && 'completedashspan'}`}><b>3</b></span>
                                        <h6>Service Selection</h6>
                                        <div className='hoverdashshow'>
                                            <div className='d-flex flex-column dashshowersspan'>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["engagement-overview"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["engagement-overview"] === "not-started" && onboardStatus["engagement-overview"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["engagement-overview"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["engagement-overview"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["engagement-overview"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["engagement-overview"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Engagement Overview
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This section is to confirm the type of support you need during onboarding.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid size={{md:2.4}} container flexDirection="column" alignItems="center" className="progress_dash">
                                        <ProgressBar now={progressBar.placement_onboarding} className='progress_divdashses'/>
                                        <span className={`rownderspan ${progressBar.placement_onboarding === 100 && 'completedashspan'}`}><b>4</b></span>
                                        <h6>Placement Onboarding</h6>
                                        <div className='hoverdashshow'>
                                            <div className='d-flex flex-column dashshowersspan'>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["jobseeker-classification"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["jobseeker-classification"] === "not-started" && onboardStatus["jobseeker-classification"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["jobseeker-classification"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["jobseeker-classification"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["jobseeker-classification"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["jobseeker-classification"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Jobseeker Classification
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                Classify your jobseeker status to personalize placement support.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["setup-servicenow-interview"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["setup-servicenow-interview"] === "not-started" && onboardStatus["setup-servicenow-interview"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["setup-servicenow-interview"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["setup-servicenow-interview"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["setup-servicenow-interview"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["setup-servicenow-interview"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Setup ServiceNow Interview
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task will unlock once you've completed the earlier steps and is used to schedule your ServiceNow interview.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["servicenow-interview-evaluation"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["servicenow-interview-evaluation"] === "not-started" && onboardStatus["servicenow-interview-evaluation"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["servicenow-interview-evaluation"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["servicenow-interview-evaluation"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["servicenow-interview-evaluation"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["servicenow-interview-evaluation"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        ServiceNow Interview Evaluation
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This step becomes available after your ServiceNow interview and is used for evaluation.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["past-payment-health-approval"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["past-payment-health-approval"] === "not-started" && onboardStatus["past-payment-health-approval"] !== "suspended"? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["past-payment-health-approval"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["past-payment-health-approval"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["past-payment-health-approval"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["past-payment-health-approval"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Past Payment Health
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is to provide information on any past payment or financial issues of you.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["application-final-review"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["application-final-review"] === "not-started" && onboardStatus["application-final-review"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["application-final-review"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["application-final-review"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["application-final-review"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["application-final-review"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Application Final Review
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This step is a final review of your application before proceeding to placement.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["placement-agreement"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["placement-agreement"] === "not-started" && onboardStatus["placement-agreement"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["placement-agreement"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["placement-agreement"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["placement-agreement"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["placement-agreement"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Placement Agreement
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is to review and sign the placement agreement .
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["communication-eligibility"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["communication-eligibility"] === "not-started" && onboardStatus["communication-eligibility"] !== "suspended"? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["communication-eligibility"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["communication-eligibility"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["communication-eligibility"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["communication-eligibility"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Communication Eligibility
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This task is to confirm whether you're eligible to receive program communication.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                                <div className='inner_contdiv_dash'>
                                                    <span className={onboardStatus && onboardStatus["admission-fees-payment"] === "skipped" ? "skippend_strike":""}>
                                                        {onboardStatus && onboardStatus["admission-fees-payment"] === "not-started" && onboardStatus["admission-fees-payment"] !== "suspended" ? 
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>:
                                                        onboardStatus["admission-fees-payment"] === "in-progress" ?
                                                        <img src={require('../../../assets/circleprocess.svg').default} alt=''/>:
                                                        onboardStatus["admission-fees-payment"] === "completed" ?
                                                        <img src={require('../../../assets/circlecheck.svg').default} alt=''/>:
                                                        onboardStatus["admission-fees-payment"] === "rejected" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        onboardStatus["admission-fees-payment"] === "suspended" ?
                                                        <img src={require('../../../assets/circleclose.svg').default} alt=''/>:
                                                        <img src={require('../../../assets/circlelock.svg').default} alt=''/>}
                                                        Admission Fees
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="left"
                                                        className='tooltipsmain_dash'
                                                        overlay={
                                                            <Tooltip id="tooltip-top" className='tooltipsmain_dash'>
                                                                This step is to review and confirm any admission fees that may apply.
                                                            </Tooltip>
                                                        }
                                                        >
                                                        <div className='tooltip_divsdash'>
                                                            <FontAwesomeIcon className='tooltip_icons' icon={faExclamationCircle} />
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid size={{md:2.4}} container flexDirection="column" alignItems="center" className="progress_dash">
                                        <span className={`rownderspan ${progressBar.placement_onboarding === 100 && 'completedashspan'}`}><b><FontAwesomeIcon icon={faCircleCheck} /></b></span>
                                        <h6>Completed</h6>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {loading ? 
                            <div className='d-flex justify-content-center w-100'>
                                <img style={{ width: "80px" }} src={require('../../../assets/pinkloader.gif')} alt='Loading...' />
                            </div> :<>
                            <Grid size={{xs:12}} container justifyContent="space-between">
                                {permissionData && permissionData?.manage_job_posts === true && permissionData?.manage_job_post_viewer === true ?
                                <Grid size={{xs:6.9}} container className="mt-3">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100" alignContent="flex-start">
                                        <h5 className='fw-semibold mt-2'>Recently Opened Jobs</h5>
                                            {jobslist && jobslist.length > 0 ? (<>
                                                {visibleJobs?.map((jobslist,index) => (
                                                    <Grid size={{xs:12}} container key={index} justifyContent="space-between" className="py-2 last_borderdash">
                                                        <Grid container className="titleadmin_joblist mb-0">
                                                            {jobslist && jobslist.source === "LinkedIn" ? 
                                                                <img src={require("../../../assets/sample/linkedin.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "Monster" ?
                                                                <img src={require("../../../assets/sample/monster.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "Indeed" ?
                                                                <img src={require("../../../assets/sample/indeed.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "Glassdoor" ?
                                                                <img src={require("../../../assets/sample/glassdoor.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "Dice" ?
                                                                <img src={require("../../../assets/sample/dice.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "Techfetch" ?
                                                                <img src={require("../../../assets/sample/techfetch.svg").default} alt='Job Icon'/>:
                                                                jobslist.source === "TekP Intelligence" ?
                                                                <img src={require("../../../assets/sample/tekp.svg").default} alt='Job Icon'/>:
                                                                <img src={require("../../../assets/sample/others.svg").default} alt='Job Icon'/>}
                                                            
                                                            <div className='jobboard_listtitle'>
                                                                <Link className='link_joblist' target="_blank" to={`/job-board/job-details/${jobslist.job_id}?job-list`}><h6 className='tittle_jobswrap' style={{color:"unset"}}>{jobslist.title}</h6></Link>
                                                                <small className='mb-0'>{jobslist && jobslist.location && jobslist.location !== "" && jobslist.location !== "unknown" && jobslist.location}{jobslist && jobslist.location && jobslist.location !== "" && jobslist.location !== "unknown" && jobslist.recruiter_company && jobslist.recruiter_company !== "" && jobslist.recruiter_company !== "unknown" && ' -'} {jobslist && jobslist.recruiter_company && jobslist.recruiter_company !== "" && jobslist.recruiter_company !== "unknown" && jobslist.recruiter_company }</small>
                                                            </div>
                                                        </Grid>
                                                        <Grid container alignItems="center" justifyContent="flex-end" className="endcardjobboard">
                                                            {jobslist && jobslist.recruiter_email && jobslist.recruiter_email[0] !== "" && jobslist.recruiter_email[0] !== "unknown" &&
                                                                <span className="hover_showes">
                                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                                    <span>
                                                                    {jobslist.recruiter_email[0]}
                                                                    {jobslist.recruiter_email.length > 1 && " ..."}
                                                                    <button
                                                                        onClick={() => {
                                                                        const copyText = jobslist.recruiter_email.join(", ");
                                                                        navigator.clipboard.writeText(copyText);
                                                                        toast("Mail ID(s) copied to clipboard");
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faCopy} />
                                                                    </button>
                                                                    </span>
                                                                </span>
                                                            }
                                                            {jobslist && jobslist.recruiter_phone && jobslist.recruiter_phone !== "" && jobslist.recruiter_phone !== "unknown" &&
                                                                <span className='hover_showes'><FontAwesomeIcon icon={faPhone} />
                                                                    <span>{jobslist.recruiter_phone} <button onClick={() => {navigator.clipboard.writeText(`${jobslist.recruiter_phone}`); toast('Phone Number copied to clipboard'); }}><FontAwesomeIcon icon={faCopy} /></button></span>
                                                                </span>
                                                            }
                                                            <small className='d-flex align-items-center'>
                                                                {jobslist && jobslist.is_saved === true ?
                                                                <span className='save_jobsspan me-2' onClick={() => handleRemoveSave(jobslist.id)}>
                                                                    <img src={require("../../../assets/sample/save_tagpink.svg").default} alt="save"/>
                                                                </span> :
                                                                <span className='save_jobsspan me-2' onClick={() => handleSave(jobslist.id)}>
                                                                    <img src={require("../../../assets/sample/save_tagblack.svg").default} alt="save"/>
                                                                </span>}
                                                            </small>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                                {jobslist.length > 3 && (
                                                    <Grid size={{xs:12}} className="d-flex justify-content-center mt-1 mb-1">
                                                        <button
                                                            className="btn btn-link p-0 text-decoration-none fw-semibold"
                                                            onClick={() => setShowAll(prev => !prev)}
                                                        >
                                                            {showAll ? "Show Less ▲" : `Show More (${jobslist.length - 3} more) ▼`}
                                                        </button>
                                                    </Grid>
                                                )}</>
                                            ) : (
                                                <div className="w-100 d-flex justify-content-center mt-3">
                                                    <p>No data to display</p>
                                                </div>
                                            )}
                                    </Grid>
                                </Grid>:
                                <Grid size={{xs:6.9}} container className="mt-3">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100" flexDirection="column" alignContent="flex-start">
                                        <h5 className='fw-semibold mt-2'>Recently Opened Jobs</h5>
                                        <h2 className='w-100 text-center mt-2 mb-0' style={{color:"#1A5298"}}><FontAwesomeIcon icon={faLock} /></h2>
                                    </Grid>
                                </Grid>
                                }
                                {permissionData && permissionData?.manage_knowledge_article === true ?
                                <Grid size={{xs:4.9}} container className="mt-3" alignContent="flex-start">
                                    <Grid size={{xs:12}} container alignContent="flex-start" className="border_recentdashboard h-100" >
                                    <h5 className='fw-semibold mt-2 mb-0'>Recently Opened Article</h5>
                                        {articles && articles.length > 0 ? (<>
                                            {visibleArticles?.map((article) => (
                                            <Grid size={{xs:12}} key={article.id} container justifyContent="space-between" className="py-2 last_borderdash">
                                                <Grid size={{sm:12}} container justifyContent="center" className="knowledge_lister">
                                                    <Grid size={{xs:12}} container className="span_griders" justifyContent="space-between">
                                                        <span className='ps-0'><FontAwesomeIcon icon={faUser} className='icon_kn'/><small>Author:</small>{article.author.name}</span>
                                                        <span>{article.view_date_time}</span>
                                                    </Grid>
                                                    <Link style={{textDecoration:"none"}} className='questionskn' target='_blank' to={`/knowledge-article/${article.format_id}`}><b className="articleformat_id">{article.format_id}</b>{article.title}</Link>
                                                    <span className='w-100' style={{wordBreak: "break-word"}}>
                                                    {article.short_description
                                                        ? article.short_description.slice(0, 300) + (article.short_description.length > 100 ? "..." : "")
                                                        : "No Data"}
                                                    </span>
                                                </Grid>
                                            </Grid>
                                            ))}
                                            {articles.length > 3 && (
                                                <Grid size={{xs:12}} className="d-flex justify-content-center mt-1 mb-1">
                                                    <button
                                                        className="btn btn-link p-0 text-decoration-none fw-semibold"
                                                        onClick={() => setShowAllArticles(prev => !prev)}
                                                    >
                                                        {showAllArticles ? "Show Less ▲" : `Show More (${articles.length - 3} more) ▼`}
                                                    </button>
                                                </Grid>
                                            )}</>
                                        ) : (
                                            <div className="w-100 d-flex justify-content-center mt-3">
                                                <p>No data to display</p>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>:
                                <Grid size={{xs:4.9}} container className="mt-3">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100" flexDirection="column" alignContent="flex-start">
                                        <h5 className='fw-semibold mt-2'>Recently Opened Article</h5>
                                        <h2 className='w-100 text-center mt-2 mb-0' style={{color:"#1A5298"}}><FontAwesomeIcon icon={faLock} /></h2>
                                    </Grid>
                                </Grid>}
                                {permissionData && permissionData?.manage_my_tasks === true ?
                                <Grid size={{xs:5.8}} container className="mt-3" alignContent="flex-start">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100 py-2" alignContent="flex-start">
                                    <h5 className='fw-semibold'>My Pending Cases</h5>
                                        {caseData && caseData.length > 0 ? (
                                            caseData && caseData.map((casedata, index) => (
                                            <Grid size={{xs:12}} key={index} container justifyContent="space-between" alignItems="center" className="py-1 main_casepend_dash">
                                                <img src={require('../../../assets/circleprocess.svg').default} alt="" />
                                                <span className='fw-medium'>{casedata.format_id} {casedata?.gcase_summary?.short_description}</span>
                                                {casedata.case_type === "onboarding" ? 
                                                <Link className='task_viewlink' target="_blank" to={casedata.case_task_links || "/task/raised-cases?onboarding=true"}>View</Link>:
                                                <Link className='task_viewlink' target="_blank" to={casedata.case_task_link || "/task/raised-cases"}>View</Link>
                                                }
                                            </Grid>
                                            ))
                                        ) : (
                                            <div className="w-100 d-flex justify-content-center mt-3">
                                                <p>No data to display</p>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>:
                                <Grid size={{xs:5.8}} container className="mt-3">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100" flexDirection="column" alignContent="flex-start">
                                        <h5 className='fw-semibold mt-2'>My Pending Cases</h5>
                                        <h2 className='w-100 text-center mt-2 mb-0' style={{color:"#1A5298"}}><FontAwesomeIcon icon={faLock} /></h2>
                                    </Grid>
                                </Grid>}
                                {permissionData && permissionData?.manage_my_tasks === true ?
                                <Grid size={{xs:5.8}} container className="mt-3" alignContent="flex-start">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100 py-2" alignContent="flex-start">
                                    <h5 className='fw-semibold'>My Pending Tasks</h5>
                                        {taskData && taskData.length > 0 ? (
                                            taskData && taskData.map((taskdata, index) => (
                                            <Grid size={{xs:12}} key={index} container justifyContent="space-between" alignItems="center" className="py-1 main_casepend_dash">
                                                <img src={require('../../../assets/circleprocess.svg').default} alt="" />
                                                <span className='fw-medium'>{taskdata.format_id} {taskdata.task_title}</span>
                                                <Link className='task_viewlink' target="_blank" to={ taskdata.assigned_user_id !== null ? `/task/placement-agreement/${taskdata.assigned_user_id}/${taskdata.id}` : "/task/my-task"}>View</Link>
                                            </Grid>
                                            ))
                                        ) : (
                                            <div className="w-100 d-flex justify-content-center mt-3">
                                                <p>No data to display</p>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>:
                                <Grid size={{xs:5.8}} container className="mt-3">
                                    <Grid size={{xs:12}} container className="border_recentdashboard h-100" flexDirection="column" alignContent="flex-start">
                                        <h5 className='fw-semibold mt-2'>My Pending Tasks</h5>
                                        <h2 className='w-100 text-center mt-2 mb-0' style={{color:"#1A5298"}}><FontAwesomeIcon icon={faLock} /></h2>
                                    </Grid>
                                </Grid>}
                            </Grid>
                        </>}
                    </Grid>
                :
                    <Grid size={{xs:12}} container justifyContent="flex-start" className="jsp-card-outer">
                        <Grid size={{xs:12}} container className="jsp-card">
                            <Grid size={{xs:12}} container justifyContent="space-between" alignItems="center" className="jsp-header">
                                <div className="jsp-header-left">
                                    <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                                            <h3>Jobseekers Report </h3>
                                            <span><b onClick={()=>navigate("/dashboard")}>Dashboard</b> <FontAwesomeIcon className='angleright' icon={faAngleRight} /> Jobseekers Report</span>
                                    </Grid>
                                </div>
                                <span className="jsp-asof">As of {pipelineAsOf.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            </Grid>

                            {reportError && !reportData ? (
                                <div className="jsp-body" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 12, width: '100%' }}>
                                    <span className="jsp-panel-hint">{reportError}</span>
                                    <button type="button" className="jsp-dropdown-btn" onClick={refetchReport}>Retry</button>
                                </div>
                            ) : !reportData ? (
                                <div className="jsp-body" style={{ alignItems: 'center', justifyContent: 'center', minHeight: 280, width: '100%' }}>
                                    <img src={require('../../../assets/pinkloader.gif')} alt="" style={{ width: 48 }} />
                                </div>
                            ) : (
                            <Grid size={{xs:12}} container spacing={{ xs: 1.5, md: 3.5 }} className="jsp-body" style={{ opacity: reportLoading ? 0.6 : 1, transition: 'opacity 150ms ease', pointerEvents: reportLoading ? 'none' : 'auto' }}>
                                {/* LEFT — Intake trend */}
                                <Grid size={{md:5,xs:12}} className="jsp-left">
                                    <div className="jsp-left-header">
                                        <div>
                                            <h5 className="jsp-section-title">Jobseekers Intake </h5>
                                        </div>
                                        {/* DROPDOWN on the right side */}
                                        <div className="jsp-dropdown-wrapper" ref={dropdownRef}>
                                            <button 
                                                className={`jsp-dropdown-btn ${isDropdownOpen ? 'open' : ''}`}
                                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            >
                                                <span>{currentFilterLabel}</span>
                                                <FontAwesomeIcon icon={faChevronDown} className={`jsp-dropdown-icon ${isDropdownOpen ? 'rotate' : ''}`} />
                                            </button>
                                            {isDropdownOpen && (
                                                <div className="jsp-dropdown-menu">
                                                    {INTAKE_DROPDOWN_FILTERS.map((filter) => (
                                                        <button
                                                            key={filter.key}
                                                            className={`jsp-dropdown-item ${intakeFilter === filter.key ? 'active' : ''}`}
                                                            onClick={() => handleFilterSelect(filter.key)}
                                                        >
                                                            {filter.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {isIntakeCustom && (
                                        <div className="jsp-daterange-row">
                                            <input type="date" value={intakeCustomStart} max={intakeCustomEnd} onChange={(e) => setIntakeCustomStart(e.target.value)} />
                                            <span>to</span>
                                            <input type="date" value={intakeCustomEnd} min={intakeCustomStart} onChange={(e) => setIntakeCustomEnd(e.target.value)} />
                                        </div>
                                    )}

                                    <div className="jsp-stat-mainrow">
                                        <span className="jsp-stat-value">{intakeStats.total}</span>
                                        <span className="jsp-stat-range">{intakeStats.rangeLabel}</span>
                                    </div>



                                    {/* CHART */}
                                    <div className="jsp-chart-wrapper">
                                        {/* CHART TOP STATS */}
                                        <div className="jsp-chart-top-stats">
                                            <div className="jsp-chart-top-stat">
                                                <span className="number">{intakeStats.average}</span>
                                                <span className="label">Avg / {intakeStats.granularity === 'Yearly' ? 'Year'
                                                    : intakeStats.granularity === 'Half-Yearly' ? 'Half-Year'
                                                    : intakeStats.granularity === 'Monthly' ? 'Month'
                                                    : intakeStats.granularity === 'Weekly' ? 'Week'
                                                    : 'Day'}</span>
                                            </div>
                                            <div className="jsp-chart-top-stat">
                                                <span className="number">{intakeStats.peakValue}</span>
                                                <span className="label">Peak on <strong>{intakeStats.peakLabel}</strong></span>
                                            </div>
                                        </div>

                                        {/* CHART */}
                                        <div ref={pipelineChartRef} className="jsp-chart" />

                                        {/* CHART LEGEND */}
                                        <div className="jsp-legend">
                                            <span className="jsp-legend-item">
                                                <i className="jsp-legend-dot actual" />
                                                {intakeStats.granularity} actual
                                            </span>
                                            
                                        </div>
                                    </div>
                                </Grid>

                                {/* RIGHT — Quick filters, tabs, breakdowns & records */}
                                <Grid size={{md:7,xs:12}} className="jsp-right">
                                    {/* Cross-cutting filters — apply to every tab below (and the
                                        records list), exactly like the lane view's Onboarding Hold /
                                        Training Status dropdowns. Not tabs: switching Stage/Visa/
                                        Manager never resets these. Hidden by default and revealed
                                        via this toggle so the panel isn't showing filter controls
                                        no one has touched yet. */}
                                    {showFilters && (
                                    <div className="jsp-quickfilters">
                                        <div className="jsp-quickfilter">
                                            <label className="jsp-quickfilter-label" id="jsp-hold-filter-label">Onboarding Hold</label>
                                            <div className="jsp-segmented" role="group" aria-labelledby="jsp-hold-filter-label">
                                                {EASY_FILTER_MODES.map((mode) => (
                                                    <button
                                                        key={mode.key}
                                                        type="button"
                                                        className={`jsp-segmented-option${holdMode === mode.key ? ' active' : ''}`}
                                                        onClick={() => setHoldMode(mode.key)}
                                                    >
                                                        {mode.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <span className="jsp-quickfilter-caption">{standardCount} Standard · {onHoldCount} On Hold</span>
                                        </div>

                                        <div className="jsp-quickfilter">
                                            <label className="jsp-quickfilter-label" id="jsp-training-filter-label">Training Status</label>
                                            <div className="jsp-segmented" role="group" aria-labelledby="jsp-training-filter-label">
                                                {EASY_FILTER_MODES.map((mode) => (
                                                    <button
                                                        key={mode.key}
                                                        type="button"
                                                        className={`jsp-segmented-option${trainingMode === mode.key ? ' active' : ''}`}
                                                        onClick={() => setTrainingMode(mode.key)}
                                                    >
                                                        {mode.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <span className="jsp-quickfilter-caption">
                                                {trainingBreakdown.map((row) => `${row.count} ${row.value}`).join(' · ') || 'No records'}
                                            </span>
                                        </div>
                                    </div>
                                    )}

                                    <div className="jsp-tabs-row">
                                        <div className="jsp-tabs-scroll" ref={tabsScrollRef}>
                                            {REPORT_TABS.map((t) => (
                                                <button
                                                    key={t.key}
                                                    type="button"
                                                    className={`jsp-tab${reportTab === t.key ? ' active' : ''}`}
                                                    onClick={() => setReportTab(t.key)}
                                                >
                                                    {t.label}
                                                </button>
                                            ))}
                                        </div>
                                        {showTabArrows && (
                                            <>
                                                <button type="button" className="jsp-tabs-arrow jsp-tabs-arrow-left" onClick={() => scrollTabs(-1)}>◀</button>
                                                <button type="button" className="jsp-tabs-arrow jsp-tabs-arrow-right" onClick={() => scrollTabs(1)}>▶</button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className={`jsp-filter-toggle-btn${showFilters ? ' active' : ''}`}
                                            onClick={() => setShowFilters((v) => !v)}
                                        >
                                            <FontAwesomeIcon icon={faFilter} />
                                            Filters
                                            {(holdMode !== 'include' || trainingMode !== 'include') && <span className="jsp-filter-dot" />}
                                        </button>
                                    </div>

                                    <div className="jsp-tab-panel">
                                        {reportTab === 'stage' && showAllSubStages && (
                                            <>
                                                <div className="jsp-panel-headrow">
                                                    <div className="jsp-panel-titlewrap">
                                                        <h6 className="jsp-panel-title">All sub-stages, ranked</h6>
                                                        <button type="button" className="jsp-back-link" onClick={handleExitAllSubStages}>← Back to stages</button>
                                                    </div>
                                                    <span className="jsp-panel-hint">Every sub-stage across every stage, in stage order</span>
                                                </div>
                                                <div className="jsp-breakdown-list jsp-allsub-list">
                                                    {allSubStagesFlat.map((seg) => (
                                                        <button
                                                            type="button"
                                                            key={`${seg.stage}::${seg.label}`}
                                                            className={`jsp-allsub-row${activeSegment && activeSegment.type === 'subStage' && activeSegment.stage === seg.stage && activeSegment.value === seg.label ? ' active' : ''}`}
                                                            onClick={() => toggleSubStage(seg.stage, seg.label)}
                                                        >
                                                            <span className="jsp-allsub-swatch" style={{ background: seg.color }} />
                                                            <span className="jsp-allsub-main">
                                                                <span className="jsp-allsub-name">
                                                                    <span className="jsp-allsub-labeltext">{seg.label}</span>
                                                                    <span className="jsp-allsub-stagetag">{seg.stage}</span>
                                                                </span>
                                                                <span className="jsp-breakdown-track">
                                                                    <span className="jsp-breakdown-fill" style={{ width: `${seg.pct}%`, background: seg.color }} />
                                                                </span>
                                                            </span>
                                                            <span className="jsp-breakdown-count">
                                                                <FontAwesomeIcon icon={faUser} className="jsp-breakdown-stat-icon" />
                                                                <span className="jsp-breakdown-num">{seg.count}</span>
                                                                <span className="jsp-breakdown-pct">{seg.pct}%</span>
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {reportTab === 'stage' && !showAllSubStages && !expandedStage && (
                                            <>
                                                <div className="jsp-panel-headrow">
                                                    <h6 className="jsp-panel-title">Volume by Stage, split by sub-stage</h6>
                                                    <div className="jsp-panel-actions">
                                                        <button type="button" className="jsp-back-link" onClick={handleViewAllSubStages}>View all sub-stages</button>
                                                        <span className="jsp-panel-hint">Click a stage to open its sub-stages</span>
                                                    </div>
                                                </div>
                                                <div className="jsp-breakdown-list jsp-stage-list">
                                                    {stageRows.map((row) => (
                                                        <button
                                                            type="button"
                                                            key={row.stage}
                                                            className="jsp-stage-row"
                                                            onClick={() => handleStageRowClick(row.stage)}
                                                        >
                                                            <span className="jsp-breakdown-label">{row.stage}</span>
                                                            <div className="jsp-stage-track">
                                                                {row.segments.map((seg) => (
                                                                    <div
                                                                        key={seg.label}
                                                                        className="jsp-stage-segment"
                                                                        style={{ width: `${Math.max(seg.pct, seg.count > 0 ? 2 : 0)}%`, background: seg.color }}
                                                                        onClick={(e) => { e.stopPropagation(); handleSegmentClick(row.stage, seg.label); }}
                                                                    >
                                                                        <span className="jsp-segment-tip">{seg.label} - {seg.count}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <span className="jsp-breakdown-count">
                                                                <FontAwesomeIcon icon={faUser} className="jsp-breakdown-stat-icon" />
                                                                <span className="jsp-breakdown-num">{row.count}</span>
                                                                <span className="jsp-breakdown-pct">{row.pct}%</span>
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {reportTab === 'stage' && !showAllSubStages && expandedStage && currentStageRow && (
                                            <>
                                                <div className="jsp-panel-headrow">
                                                    <div className="jsp-panel-titlewrap">
                                                        <h6 className="jsp-panel-title">{expandedStage} — sub-stages</h6>
                                                        <button type="button" className="jsp-back-link" onClick={handleCollapseStage}>← All stages</button>
                                                    </div>
                                                    <span className="jsp-panel-hint">Click a sub-stage to see who's there, or a stage again to collapse</span>
                                                </div>
                                                <div className="jsp-breakdown-list jsp-substage-list">
                                                    {currentStageRow.segments.map((seg) => (
                                                        <button
                                                            type="button"
                                                            key={seg.label}
                                                            className={`jsp-breakdown-row${activeSegment && activeSegment.type === 'subStage' && activeSegment.stage === expandedStage && activeSegment.value === seg.label ? ' active' : ''}`}
                                                            onClick={() => toggleSubStage(expandedStage, seg.label)}
                                                        >
                                                            <span className="jsp-breakdown-label">{seg.label}</span>
                                                            <div className="jsp-breakdown-track">
                                                                <div className="jsp-breakdown-fill" style={{ width: `${seg.pct}%`, background: seg.color }} />
                                                            </div>
                                                            <span className="jsp-breakdown-count">
                                                                <FontAwesomeIcon icon={faUser} className="jsp-breakdown-stat-icon" />
                                                                <span className="jsp-breakdown-num">{seg.count}</span>
                                                                <span className="jsp-breakdown-pct">{seg.pct}%</span>
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {reportTab === 'visa' && (
                                            <>
                                                <div className="jsp-panel-headrow">
                                                    <span className="jsp-panel-hint">Click a slice or a row to filter</span>
                                                </div>
                                                <div className="jsp-split-panel">
                                                    <div className="jsp-donut-wrap">
                                                        <div ref={visaChartRef} className="jsp-donut-chart" />
                                                        <div className="jsp-donut-center">
                                                            <span className="jsp-donut-center-value">
                                                                {activeSegment && activeSegment.type === 'visa'
                                                                    ? visaBreakdown.find((r) => r.value === activeSegment.value)?.count ?? 0
                                                                    : reportTotal}
                                                            </span>
                                                            <span className="jsp-donut-center-label">
                                                                {activeSegment && activeSegment.type === 'visa' ? activeSegment.value : 'Total'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="jsp-breakdown-list jsp-split-list">
                                                        {visaBreakdown.map((row, idx) => (
                                                            <button
                                                                type="button"
                                                                key={row.value}
                                                                className={`jsp-breakdown-row${activeSegment && activeSegment.type === 'visa' && activeSegment.value === row.value ? ' active' : ''}`}
                                                                onClick={() => handleBreakdownRowClick('visa', row.value)}
                                                            >
                                                                <span className="jsp-breakdown-label">
                                                                    <i className="jsp-swatch-dot" style={{ background: buildThemePalette(visaBreakdown.length)[idx] }} />
                                                                    {row.value}
                                                                </span>
                                                                <div className="jsp-breakdown-track">
                                                                    <div className="jsp-breakdown-fill" style={{ width: `${row.pct}%`, background: buildThemePalette(visaBreakdown.length)[idx] }} />
                                                                </div>
                                                                <span className="jsp-breakdown-count">
                                                                    <FontAwesomeIcon icon={faUser} className="jsp-breakdown-stat-icon" />
                                                                    <span className="jsp-breakdown-num">{row.count}</span>
                                                                    <span className="jsp-breakdown-pct">{row.pct}%</span>
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {reportTab === 'manager' && (
                                            <>
                                                <div className="jsp-panel-headrow">
                                                    <span className="jsp-panel-hint">Ranked by caseload — click a row to filter</span>
                                                </div>
                                                <div className="jsp-manager-list">
                                                    {managerBreakdown.map((row, idx) => {
                                                        const label = row.value;
                                                        const isActive = activeSegment && activeSegment.type === 'manager' && activeSegment.value === row.value;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={row.value}
                                                                className={`jsp-manager-row${isActive ? ' active' : ''}${idx === 0 ? ' top' : ''}`}
                                                                onClick={() => handleBreakdownRowClick('manager', row.value)}
                                                            >
                                                                <span className="jsp-manager-rank">{idx + 1}</span>
                                                                <img className="jsp-manager-avatar" src={row.photo || DEFAULT_PROFILE_IMAGE} alt="" />
                                                                <span className="jsp-manager-main">
                                                                    <span className="jsp-manager-name">
                                                                        <span className="jsp-manager-name-text">{label}</span>
                                                                        {idx === 0 && <span className="jsp-manager-toppill">Highest load</span>}
                                                                    </span>
                                                                    <span className="jsp-manager-track">
                                                                        <span className="jsp-manager-fill" style={{ width: `${row.pct}%` }} />
                                                                    </span>
                                                                </span>
                                                                <span className="jsp-manager-count">
                                                                    <strong>{row.count}</strong>
                                                                    <em>{row.pct}%</em>
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}

                                    </div>

                                    {segmentPanel ? (
                                        <>
                                            <div className="jsp-records-header">
                                                <span className="jsp-panel-title">Who's in {segmentPanel.label}</span>
                                                <span className="jsp-records-count">{segmentPanel.rows.length} of {segmentPanel.total}</span>
                                            </div>
                                            <div className="jsp-records-wrap">
                                                <div className="jsp-records-list">
                                                    {segmentPanel.rows.map((r) => (
                                                        <div key={r.id} className="jsp-record-row">
                                                            <img className="jsp-record-avatar" src={r.profile || DEFAULT_PROFILE_IMAGE} alt="" />
                                                            <Link to={`/user/jobseeker-details/${r.id}`} target="_blank" className="jsp-record-main jsp-record-link">
                                                                <span className="jsp-record-name">{r.name}</span>
                                                                <span className="jsp-record-idline">{r.formatId}</span>
                                                            </Link>
                                                            <div className="jsp-record-badges">
                                                                {r.visa && <span className="jsp-badge">{r.visa}</span>}
                                                                {r.manager && <span className="jsp-badge">{r.manager}</span>}
                                                                {r.onHold && <span className="jsp-badge onhold">On Hold</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {segmentPanel.loading && segmentPanel.rows.length === 0 && (
                                                        <div className="jsp-records-empty">Loading...</div>
                                                    )}
                                                    {!segmentPanel.loading && segmentPanel.error && (
                                                        <div className="jsp-records-empty">{segmentPanel.error}</div>
                                                    )}
                                                    {!segmentPanel.loading && !segmentPanel.error && segmentPanel.rows.length === 0 && (
                                                        <div className="jsp-records-empty">No jobseekers match</div>
                                                    )}
                                                    {segmentPanel.hasMore && <div ref={segmentSentinelRef} />}
                                                    {segmentPanel.loading && segmentPanel.rows.length > 0 && (
                                                        <div className="jsp-records-empty">Loading more...</div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="jsp-records-header">
                                            <span className="jsp-panel-title">{reportTotal} jobseeker{reportTotal === 1 ? '' : 's'} match the current filters</span>
                                            <Link to="/user/jobseekers" className="jsp-back-link">View Full List</Link>
                                        </div>
                                    )}
                                </Grid>
                            </Grid>
                            )}
                        </Grid>

                        
                    </Grid>
                }</>}
            </Grid>
        </div>
    </div>
  )
}

export default Jobseeker